import type { ReactNode } from 'react';
import { cx } from '../ui/primitives';

/**
 * Icon actions for the tab list.
 *
 * Five text buttons per row did not fit, and shrinking them only made them hard
 * to read. The secondary actions become icons; the one an operator comes to the
 * screen for keeps its words.
 *
 * Every icon carries a visible tooltip **and** an accessible name, because an
 * icon alone is a guess — a pencil could mean edit the document or edit the
 * request, and the difference matters here.
 */

function Glyph({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      {children}
    </svg>
  );
}

export function EyeIcon() {
  return (
    <Glyph>
      <path d="M1.8 10S4.9 4.6 10 4.6 18.2 10 18.2 10 15.1 15.4 10 15.4 1.8 10 1.8 10Z" />
      <circle cx="10" cy="10" r="2.4" />
    </Glyph>
  );
}

export function PencilIcon() {
  return (
    <Glyph>
      <path d="M13.4 3.6a1.7 1.7 0 0 1 2.4 2.4L7.3 14.5 4 15.4l.9-3.3Z" />
      <path d="M12.2 4.8 14.6 7.2" />
    </Glyph>
  );
}

export function CopyIcon() {
  return (
    <Glyph>
      <rect x="7" y="7" width="8.6" height="8.6" rx="1.6" />
      <path d="M12.4 4.4H5.9c-.8 0-1.5.7-1.5 1.5v6.5" />
    </Glyph>
  );
}

export function ExternalIcon() {
  return (
    <Glyph>
      <path d="M11.2 4.4h4.4v4.4" />
      <path d="M15.6 4.4 9 11" />
      <path d="M13.4 11.6v3.1c0 .5-.4.9-.9.9H5.3a.9.9 0 0 1-.9-.9V7.5c0-.5.4-.9.9-.9h3.1" />
    </Glyph>
  );
}

const SHELL =
  'inline-flex h-8 w-8 items-center justify-center rounded-input border border-line ' +
  'text-ink-muted transition-colors hover:border-line-strong hover:text-ink ' +
  'focus-visible:ring-2 focus-visible:ring-accent/40';

/** An icon button. `label` is both the tooltip and the accessible name. */
export function IconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" onClick={onClick} title={label} aria-label={label} className={SHELL}>
      {children}
    </button>
  );
}

/** The same shell as a link, for actions that leave the application. */
export function IconLink({
  label,
  href,
  children,
}: {
  label: string;
  href: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={label}
      aria-label={label}
      className={cx(SHELL, 'no-underline')}
    >
      {children}
    </a>
  );
}
